import React, { useState } from "react";
import { products } from "../data/content";
import "./ProductGallery.css";

export default function ProductGallery({
  searchQuery = "",
  onProductSelect,
  onAddToCart,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [quantities, setQuantities] = useState(
    products.reduce((acc, product) => {
      acc[product.id] = 1;
      return acc;
    }, {}),
  );

  const filteredProducts = products.filter((product) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      product.title.toLowerCase().includes(query) ||
      product.subtitle.toLowerCase().includes(query) ||
      product.caption.toLowerCase().includes(query)
    );
  });

  function handleToggle(product) {
    setExpandedId((prev) => {
      const next = prev === product.id ? null : product.id;
      if (onProductSelect) onProductSelect(next ? product : null);
      return next;
    });
  }

  function updateQuantity(productId, direction, e) {
    if (e) e.stopPropagation();
    setQuantities((prev) => {
      const current = prev[productId] || 1;
      const next = direction === "up" ? current + 1 : Math.max(1, current - 1);
      return { ...prev, [productId]: next };
    });
  }

  function parsePrice(price) {
    if (typeof price === "number") return price;
    const match = String(price)
      .replace(/,/g, "")
      .match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : 0;
  }

  function formatCurrency(value) {
    return `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  }

  function handleOrder(product, e) {
    e.stopPropagation();
    // Set the selected product and scroll to order form (don't open WhatsApp yet)
    onProductSelect?.(product);
    setTimeout(() => {
      document
        .getElementById("order")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }

  return (
    <section className="product-gallery" id="products">
      <div className="product-gallery-copy">
        <span className="section-eyebrow">Product Photos</span>
        <h2 className="section-title">
          See Our Wellness Packs
          <br />
          In Real Life
        </h2>
        <p className="section-sub">
          Browse the latest product images for Swathi's We BetterR Wellness.
          These photos highlight the premium packaging, herbal goodness and
          natural presentation of the powder and oil bundles.
        </p>
      </div>

      <div className="gallery-grid">
        {filteredProducts.length > 0 ? (
          filteredProducts.map((product) => {
            const isOpen = expandedId === product.id;
            return (
              <article
                key={product.id}
                className={`gallery-card ${isOpen ? "gallery-card--active" : ""}`}
                onClick={() => handleToggle(product)}
                aria-expanded={isOpen}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleToggle(product);
                  }
                }}
              >
                {/* ── Image ── */}
                <div className="gallery-img-wrap">
                  <img
                    src={`${process.env.PUBLIC_URL}${product.src}`}
                    alt={product.alt}
                    className="gallery-image"
                    loading="lazy"
                    style={
                      product.fit === "contain"
                        ? { objectFit: "contain", padding: 12, background: "transparent" }
                        : undefined
                    }
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextSibling.style.display = "flex";
                    }}
                  />
                  
                  <div
                    className="gallery-img-fallback"
                    style={{ display: "none" }}
                  >
                    <span>🌿</span>
                  </div>
                </div>

                {/* ── Card body (always visible) ── */}
                <div className="gallery-card-body">
                  {/* Row 1: Title (left) + Pack Size (right) — same baseline */}
                  <div className="product-title-row">
                    <span className="product-title">{product.title}</span>
                    <span className="product-packsize">{product.subtitle}</span>
                  </div>
                  {/* Row 2: Price block */}
                  <div className="price-block">
                    {(() => {
                      const rawPrice = parsePrice(product.price);
                      const discounted = rawPrice;
                      const original = Math.round(discounted * 1.25);
                      const save = original - discounted;
                      const percent = Math.round((save / original) * 100);
                      return (
                        <div className="price-wrap">
                          <div className="price-row">
                            <span className="original-price">
                              {formatCurrency(original)}
                            </span>
                            <span className="discount-price">
                              {formatCurrency(discounted)}
                            </span>
                          </div>
                          <div className="price-meta">
                            <span className="discount-badge">
                              {percent}% OFF
                            </span>
                            <span className="save-amount">
                              Save {formatCurrency(save)}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  {/* ── Bottom row: Quantity (left) | Add to Cart + Order Now (right) ── */}
                  <div className="card-bottom-row">
                    {/* Quantity control — left side */}
                    <div className="quantity-control">
                      <button
                        type="button"
                        className="quantity-btn"
                        onClick={(e) => updateQuantity(product.id, "down", e)}
                        aria-label={`Decrease quantity for ${product.title}`}
                      >
                        −
                      </button>
                      <span className="quantity-value">
                        {quantities[product.id]}
                      </span>
                      <button
                        type="button"
                        className="quantity-btn"
                        onClick={(e) => updateQuantity(product.id, "up", e)}
                        aria-label={`Increase quantity for ${product.title}`}
                      >
                        +
                      </button>
                    </div>
                    {/* Action buttons — right side */}
                    <div className="card-action-stack">
                      <button
                        type="button"
                        className="product-add-cart-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddToCart?.(product, quantities[product.id] || 1);
                        }}
                      >
                        + Add to Cart
                      </button>
                      <button
                        type="button"
                        className="product-order-btn"
                        onClick={(e) => handleOrder(product, e)}
                      >
                        Order Now
                      </button>
                    </div>
                  </div>
                </div>

                {/* ── Inline expanded details ── */}
                {isOpen && (
                  <div
                    className="inline-details"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="inline-close"
                      aria-label="Close details"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedId(null);
                        if (onProductSelect) onProductSelect(null);
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M15 9l-6 6M9 9l6 6" />
                      </svg>
                    </button>

                    <div className="inline-details-content">
                      <div className="inline-meta">
                        <div className="details-price">
                          {formatCurrency(parsePrice(product.price))}
                        </div>
                        <div className="details-packsize">
                          Pack size: {product.packSize}
                        </div>
                      </div>
                      <p className="details-copy">{product.details}</p>
                      <button
                        type="button"
                        className="details-order-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOrder(product, e);
                          setExpandedId(null);
                        }}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ width: 16, height: 16 }}
                        >
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        Order This Product
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })
        ) : (
          <div className="gallery-empty">
            No matching products found. Try a different search term.
          </div>
        )}
      </div>
    </section>
  );
}
